import type { RefObject } from "react";
import type { ScrollView as RNScrollView, View as RNView } from "react-native";
import { isWeb, type TamaguiElement } from "tamagui";

export type ScrollToNodeOptions = {
  /** Web scroll alignment; native always scrolls the node's top into view. */
  block?: ScrollLogicalPosition;
};

/**
 * Scroll a node into view inside its scroll container, cross-platform: web
 * `scrollIntoView`, native `measureLayout` (scroll-container-relative, so no
 * manual offset tracking) + `scrollTo`. Timing concerns (debounce, waiting for
 * layout animations) belong to the caller.
 */
export function scrollToNode(
  targetRef: RefObject<TamaguiElement | null>,
  scrollRef: RefObject<RNScrollView | null> | null,
  options?: ScrollToNodeOptions,
): void {
  const node = targetRef.current;
  if (!node) {
    return;
  }

  if (isWeb) {
    if ("scrollIntoView" in node) {
      node.scrollIntoView({
        behavior: "smooth",
        block: options?.block ?? "nearest",
      });
    }
    return;
  }

  const scrollNode = scrollRef?.current;
  if (!scrollNode) {
    return;
  }

  node.measureLayout(
    // why `as unknown as`: RN types ScrollView without the NativeMethods
    // mixin (measure/measureLayout/...) though the host component has them.
    scrollNode as unknown as RNView,
    (_x: number, y: number) => {
      scrollNode.scrollTo({ y, animated: true });
    },
    () => {},
  );
}
